import React, { useEffect, useState } from "react"
import classNames from "classnames"
import { useLocation } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { Collapse, Drawer, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material"

import { SideDrawerProps } from "@/app/components/sidenavDrawer/type"
import Icon from "@/components/icon/Icon"
import { Route } from "@/types/route"
import { CustomIconType } from "@/types/components/icon"
import { APP_ROUTES } from "@/constants/routes"
import { createAccountStore } from "@/stores/account"
import useRouter from "@/hooks/router/useRoute"

import "./sideNavDrawer.scss"

// auth 체크 함수: auth가 없으면 전부 접근 가능, 있으면 명시된 type만 접근 가능
const hasRouteAuth = (
  route: Route,
  accountType?: string,
  teamPermissions?: Record<string, boolean>,
  idfTeam?: number,
): boolean => {
  if (route.auth) {
    if (!accountType) return false
    if (!route.auth.includes(accountType as "P" | "C")) return false
  }
  if (route.permission) {
    if (!teamPermissions?.[route.permission]) return false
  }
  // 팀별 노출 조건 (예: 데이터포털 파일럿 팀)
  if (route.visibleForTeam && !route.visibleForTeam(idfTeam)) return false
  return true
}

export default function SideDrawer({ isDrawerOpen, isMobile, onClose }: SideDrawerProps) {
  // 각 부모 경로별로 열린 자식 메뉴를 관리
  const [openMenusByParent, setOpenMenusByParent] = useState<Record<string, string | null>>({})
  const router = useRouter()
  const location = useLocation()
  const { info, teamProfileInfo, selectedIdfTeam } = createAccountStore()
  const teamPermissions = teamProfileInfo?.teamPermissions as Record<string, boolean> | undefined

  // 초기 진입 및 URL 변경 시 현재 경로에 맞는 메뉴 상태로 업데이트하는 함수
  const initializeMenusFromURL = () => {
    const currentPath = location.pathname.split("?")[0]
    const newOpenMenus: Record<string, string | null> = {}

    // 모든 라우트를 재귀적으로 탐색하여 현재 경로에 해당하는 메뉴 찾기
    const findAndOpenMenus = (routes: Route[], parentPath: string = "root") => {
      routes.forEach((route) => {
        if (route.disableTreeView) return

        // 현재 경로가 이 라우트와 매칭되는지 확인
        const isCurrentPathMatched =
          currentPath === route.path ||
          (currentPath.startsWith(route.path + "/") && route.path !== "/")

        if (isCurrentPathMatched && route.children) {
          // 이 라우트를 열어야 함
          newOpenMenus[parentPath] = route.path

          // 자식 라우트들도 확인
          findAndOpenMenus(route.children, route.path)
        } else if (route.children) {
          const hasMatchingChild = route.children.some(
            (child) =>
              currentPath === child.path ||
              (currentPath.startsWith(child.path + "/") && child.path !== "/"),
          )

          if (hasMatchingChild) {
            // 이 라우트를 열어야 함
            newOpenMenus[parentPath] = route.path
            // 자식 라우트들도 재귀적으로 확인
            findAndOpenMenus(route.children, route.path)
          }
        }
      })
    }

    findAndOpenMenus(APP_ROUTES)
    setOpenMenusByParent(newOpenMenus)
  }

  // 컴포넌트 마운트 시 초기화
  useEffect(() => {
    initializeMenusFromURL()
  }, [location.pathname])

  const handleToggleMenu = (routePath: string, parentPath: string = "root") => {
    setOpenMenusByParent((prev) => ({
      ...prev,
      [parentPath]: prev[parentPath] === routePath ? null : routePath,
    }))
  }

  return (
    <>
      {/* 모바일 백드롭 */}
      {isMobile && isDrawerOpen && <div className="SideNavBackdrop" onClick={onClose} />}
      <Drawer
        className={classNames("SideNavDrawerCont", { isDrawerOpen, isMobile })}
        open={isDrawerOpen}
        variant="persistent"
        anchor="left"
        transitionDuration={300}>
        <div className="side-nav-drawer-header">
          <div className="business-logo" onClick={() => router?.push("/home")}>
            <img src="/images/logo_horizontal.png" alt="Fitogether logo" width={145} height={22} />
          </div>
        </div>
        <div className="side-nav-drawer-body">
          {APP_ROUTES.filter(
            (route) =>
              !route.disableTreeView &&
              hasRouteAuth(route, info?.type, teamPermissions, selectedIdfTeam),
          ).map((route, index) => (
            <RenderSideDrawer
              key={index}
              route={route}
              depth={1}
              parentPath="root"
              openMenusByParent={openMenusByParent}
              onToggleMenu={handleToggleMenu}
              accountType={info?.type}
              teamPermissions={teamPermissions}
              isMobile={isMobile}
              onCloseDrawer={onClose}
            />
          ))}
        </div>
        <div className="side-nav-drawer-footer">
          <img src="/images/aspire.svg" alt="Aspire logo" className="aspire-logo" />
        </div>
      </Drawer>
    </>
  )
}

function RenderSideDrawer({
  route,
  depth,
  renderForce,
  parentPath,
  openMenusByParent,
  onToggleMenu,
  accountType,
  teamPermissions,
  isMobile,
  onCloseDrawer,
  ...props
}: {
  route: Route
  depth: number
  props?: unknown[]
  renderForce?: boolean
  parentPath: string
  openMenusByParent: Record<string, string | null>
  onToggleMenu: (routePath: string, parentPath: string) => void
  accountType?: string
  teamPermissions?: Record<string, boolean>
  isMobile?: boolean
  onCloseDrawer?: () => void
}): React.ReactElement {
  const location = useLocation()
  const pathname = location.pathname
  const { selectedIdfTeam } = createAccountStore()
  const router = useRouter()
  const { t } = useTranslation()

  const isCollapsable = () => {
    if (!route.children) {
      return false
    }

    // 권한과 disableTreeView 둘 다 고려해서 보이는 자식만 필터링
    const visibleChildren = route.children?.filter(
      (child) =>
        !child.disableTreeView &&
        hasRouteAuth(child, accountType, teamPermissions, selectedIdfTeam),
    )

    return visibleChildren && visibleChildren.length > 0
  }

  // 현재 부모 경로에서 이 메뉴가 열려있는지 확인
  const isOpen = openMenusByParent[parentPath] === route.path

  // 정확한 경로 매칭을 위한 함수
  const isCurrentRoute = () => {
    const currentPath = pathname.split("?")[0]

    // 정확히 일치하거나, 현재 경로가 route.path로 시작하고 다음 문자가 '/'인 경우
    return (
      currentPath === route.path || (currentPath.startsWith(route.path + "/") && route.path !== "/")
    )
  }

  const handleClick = () => {
    if (isCollapsable()) {
      onToggleMenu(route.path, parentPath)
      return
    }

    // 외부 URL이 있으면 새 탭에서 열기 — 팀별 목적지가 정의된 라우트는 선택된 팀으로 정한다
    const externalUrl = route.externalUrlForTeam?.(selectedIdfTeam) ?? route.externalUrl
    if (externalUrl) {
      window.open(externalUrl, "_blank", "noopener,noreferrer")
      if (isMobile) onCloseDrawer?.()
      return
    }

    if (route.redirect) {
      router?.push(route.redirect)
      if (isMobile) onCloseDrawer?.()
      return
    }

    // report 의 경우 같은 path 로 이동하려고 시도하면 무효화
    if (route.path.includes("report") && route.path === pathname) {
      if (isMobile) onCloseDrawer?.()
      return
    }

    router?.push(route.path)
    if (isMobile) onCloseDrawer?.()
  }

  if (!renderForce && route.disableTreeView) {
    return <></>
  }

  return (
    <List {...props}>
      <ListItemButton
        className={classNames("snb-route-cont", `snb-route--depth-${depth}`, {
          currentRoute: isCurrentRoute(),
          collapsable: isCollapsable(),
        })}
        onClick={handleClick}
        disableRipple>
        {!!route.icon && (
          <ListItemIcon className={classNames("snb-nav-icon")}>
            <Icon type={route.icon as CustomIconType} width={20} height={20} />
          </ListItemIcon>
        )}
        <ListItemText
          className="snb-nav-text"
          primary={
            <p>
              {route.props && route.props?.marker ? (
                <Icon width={16} height={16} type={route.props?.marker as CustomIconType} />
              ) : undefined}

              {t(route.name)}

              {route.deprecated && (
                <div className="snb-deprecated-badge">{t("messages.deprecated")}</div>
              )}
            </p>
          }
        />
        {isCollapsable() && (
          <Icon
            className={classNames("nav-collapse-icon", { open: isOpen })}
            type={isOpen ? "arrow-up" : "arrow-down"}
            width={16}
            height={16}
          />
        )}
      </ListItemButton>
      {route.children && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {route.children
            .filter((child) => hasRouteAuth(child, accountType, teamPermissions, selectedIdfTeam))
            .map((child, index) => (
              <RenderSideDrawer
                key={index}
                route={child}
                depth={depth + 1}
                parentPath={route.path}
                openMenusByParent={openMenusByParent}
                onToggleMenu={onToggleMenu}
                accountType={accountType}
                teamPermissions={teamPermissions}
                isMobile={isMobile}
                onCloseDrawer={onCloseDrawer}
              />
            ))}
        </Collapse>
      )}
    </List>
  )
}
